import React, { useState, useEffect } from "react";
import { getSettings } from "../../../services/settingsService";

const Features = ({ features = {}, setFeatures }) => {
    const [featureSettings, setFeatureSettings] = useState({
        // User Registration Features
        user_registration: true,
        user_email_verification: true,
        user_phone_verification: true,
        user_profile_completion: true,
        user_medical_history: true,
        user_emergency_contacts: true,
        user_document_upload: true,
        user_consent_forms: true,
        
        // Doctor Registration Features
        doctor_registration: true,
        doctor_verification: true,
        doctor_license_verification: true,
        doctor_specialization: true,
        doctor_qualification_upload: true,
        doctor_experience_tracking: true,
        doctor_availability_management: true,
        doctor_consultation_fees: true,
        doctor_rating_system: true,
        doctor_review_system: true,
        
        // Insurance Features
        insurance_registration: true,
        insurance_verification: true,
        insurance_claim_processing: true,
        insurance_coverage_check: true,
        insurance_pre_authorization: true,
        insurance_document_upload: true,
        insurance_policy_management: true,
        insurance_claim_tracking: true,
        
        // Appointment Features
        appointment_booking: true,
        appointment_scheduling: true,
        appointment_reminders: true,
        appointment_rescheduling: true,
        appointment_cancellation: true,
        appointment_waitlist: true,
        appointment_video_call: true,
        appointment_voice_call: true,
        appointment_chat: true,
        appointment_prescription: true,
        
        // Payment Features
        payment_processing: true,
        payment_gateway_integration: true,
        payment_refund: true,
        payment_invoice_generation: true,
        payment_receipt: true,
        payment_installment: true,
        payment_wallet: true,
        payment_coupon_system: true,
        
        // Communication Features
        email_notifications: true,
        sms_notifications: true,
        push_notifications: true,
        in_app_messaging: true,
        video_calling: true,
        voice_calling: true,
        chat_system: true,
        file_sharing: true,
        
        // Medical Features
        medical_records: true,
        prescription_management: true,
        lab_reports: true,
        medical_imaging: true,
        health_tracking: true,
        medication_reminders: true,
        vital_signs_tracking: true,
        medical_alerts: true,
        
        // Admin Features
        admin_dashboard: true,
        user_management: true,
        doctor_management: true,
        appointment_management: true,
        payment_management: true,
        report_generation: true,
        analytics_dashboard: true,
        system_settings: true,
        
        // Security Features
        two_factor_authentication: true,
        data_encryption: true,
        audit_logs: true,
        role_based_access: true,
        session_management: true,
        password_policy: true,
        
        // Additional Features
        multi_language_support: true,
        dark_mode: true,
        mobile_app: true,
        offline_mode: true,
        api_integration: true,
        third_party_integrations: true,
        backup_restore: true,
        system_monitoring: true
    });

    useEffect(() => {
        if (features && Object.keys(features).length > 0) {
            setFeatureSettings(prev => ({ ...prev, ...features }));
        }
    }, [features]);

    useEffect(() => {
        getSettings().then(settings => {
            setFeatures(settings);
        });
    }, []);

    const handleFeatureToggle = (featureName) => {
        const updatedSettings = {
            ...featureSettings,
            [featureName]: !featureSettings[featureName]
        };
        setFeatureSettings(updatedSettings);
        setFeatures(updatedSettings);
    };

    const handleCategoryToggle = (categoryFeatures) => {
        const allEnabled = categoryFeatures.every(feature => featureSettings[feature]);
        const updatedSettings = { ...featureSettings };
        
        categoryFeatures.forEach(feature => {
            updatedSettings[feature] = !allEnabled;
        });
        
        setFeatureSettings(updatedSettings);
        setFeatures(updatedSettings);
    };

    const FeatureCategory = ({ title, features, description }) => {
        const categoryFeatures = features.map(f => f.key);
        const allEnabled = categoryFeatures.every(feature => featureSettings[feature]);
        const someEnabled = categoryFeatures.some(feature => featureSettings[feature]);

        return (
            <div className="dc-tabsinfo dc-tabsinfo-mt">
                <div className="dc-tabscontenttitle">
                    <h3>{title}</h3>
                    <p className="text-muted">{description}</p>
                </div>
                <div className="dc-formtheme dc-userform">
                    <div className="form-group">
                        <div className="dc-checkbox-holder" style={{
                            display: 'flex',
                            alignItems: 'center'
                        }}>
                            <label htmlFor={`category-${title.replace(/\s+/g, '-').toLowerCase()}`}>
                                <span className="dc-checkbox-text">Enable All {title}</span>
                            </label>
                            <input
                                type="checkbox"
                                id={`category-${title.replace(/\s+/g, '-').toLowerCase()}`}
                                checked={allEnabled}
                                ref={input => {
                                    if (input) input.indeterminate = someEnabled && !allEnabled;
                                }}
                                style={{
                                    width: '15px',
                                    marginTop: '-3px'
                                }}
                                onChange={() => handleCategoryToggle(categoryFeatures)}
                                className="dc-checkbox"
                            />
                        </div>
                    </div>
                    <div className="row">
                        {features.map((feature) => (
                            <div key={feature.key} className="col-md-6 col-lg-4">
                                <div className="dc-checkbox-holder" style={{
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                    <input
                                        type="checkbox"
                                        id={feature.key}
                                        checked={featureSettings[feature.key] || false}
                                        onChange={() => handleFeatureToggle(feature.key)}
                                        className="dc-checkbox" 
                                        style={{
                                            width: '15px',
                                            marginTop: '-7px'
                                        }}
                                    />
                                    <label htmlFor={feature.key}>
                                        <span className="dc-checkbox-text">{feature.name}</span>
                                    </label>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="dc-tabscontent tab-content">
            <div className="dc-personalskillshold tab-pane active fade show" id="dc-skills">
                <div className="dc-tabsinfo">
                    <div className="dc-tabscontenttitle">
                        <h3>Platform Features Configuration</h3>
                        <p className="text-muted">Enable or disable various features for your medical platform. Configure what functionality should be available to users, doctors, and administrators.</p>
                    </div>
                </div>

                <FeatureCategory
                    title="User Registration & Management"
                    description="Configure user registration and profile management features"
                    features={[
                        { key: 'user_registration', name: 'User Registration' },
                        { key: 'user_email_verification', name: 'Email Verification' },
                        { key: 'user_phone_verification', name: 'Phone Verification' },
                        { key: 'user_profile_completion', name: 'Profile Completion' },
                        { key: 'user_medical_history', name: 'Medical History' },
                        { key: 'user_emergency_contacts', name: 'Emergency Contacts' },
                        { key: 'user_document_upload', name: 'Document Upload' },
                        { key: 'user_consent_forms', name: 'Consent Forms' }
                    ]}
                />

                <FeatureCategory
                    title="Doctor Registration & Management"
                    description="Configure doctor registration and verification features"
                    features={[
                        { key: 'doctor_registration', name: 'Doctor Registration' },
                        { key: 'doctor_verification', name: 'Doctor Verification' },
                        { key: 'doctor_license_verification', name: 'License Verification' },
                        { key: 'doctor_specialization', name: 'Specialization Management' },
                        { key: 'doctor_qualification_upload', name: 'Qualification Upload' },
                        { key: 'doctor_experience_tracking', name: 'Experience Tracking' },
                        { key: 'doctor_availability_management', name: 'Availability Management' },
                        { key: 'doctor_consultation_fees', name: 'Consultation Fees' },
                        { key: 'doctor_rating_system', name: 'Rating System' },
                        { key: 'doctor_review_system', name: 'Review System' }
                    ]}
                />

                <FeatureCategory
                    title="Insurance Management"
                    description="Configure insurance registration and claim processing features"
                    features={[
                        { key: 'insurance_registration', name: 'Insurance Registration' },
                        { key: 'insurance_verification', name: 'Insurance Verification' },
                        { key: 'insurance_claim_processing', name: 'Claim Processing' },
                        { key: 'insurance_coverage_check', name: 'Coverage Check' },
                        { key: 'insurance_pre_authorization', name: 'Pre-Authorization' },
                        { key: 'insurance_document_upload', name: 'Document Upload' },
                        { key: 'insurance_policy_management', name: 'Policy Management' },
                        { key: 'insurance_claim_tracking', name: 'Claim Tracking' }
                    ]}
                />

                <FeatureCategory
                    title="Appointment Management"
                    description="Configure appointment booking and management features"
                    features={[
                        { key: 'appointment_booking', name: 'Appointment Booking' },
                        { key: 'appointment_scheduling', name: 'Appointment Scheduling' },
                        { key: 'appointment_reminders', name: 'Appointment Reminders' },
                        { key: 'appointment_rescheduling', name: 'Rescheduling' },
                        { key: 'appointment_cancellation', name: 'Cancellation' },
                        { key: 'appointment_waitlist', name: 'Waitlist Management' },
                        { key: 'appointment_video_call', name: 'Video Consultations' },
                        { key: 'appointment_voice_call', name: 'Voice Consultations' },
                        { key: 'appointment_chat', name: 'Chat Consultations' },
                        { key: 'appointment_prescription', name: 'Prescription Management' }
                    ]}
                />

                <FeatureCategory
                    title="Payment Processing"
                    description="Configure payment and billing features"
                    features={[
                        { key: 'payment_processing', name: 'Payment Processing' },
                        { key: 'payment_gateway_integration', name: 'Gateway Integration' },
                        { key: 'payment_refund', name: 'Refund Processing' },
                        { key: 'payment_invoice_generation', name: 'Invoice Generation' },
                        { key: 'payment_receipt', name: 'Receipt Generation' },
                        { key: 'payment_installment', name: 'Installment Payments' },
                        { key: 'payment_wallet', name: 'Digital Wallet' },
                        { key: 'payment_coupon_system', name: 'Coupon System' }
                    ]}
                />

                <FeatureCategory
                    title="Communication Features"
                    description="Configure communication and notification features"
                    features={[
                        { key: 'email_notifications', name: 'Email Notifications' },
                        { key: 'sms_notifications', name: 'SMS Notifications' },
                        { key: 'push_notifications', name: 'Push Notifications' },
                        { key: 'in_app_messaging', name: 'In-App Messaging' },
                        { key: 'video_calling', name: 'Video Calling' },
                        { key: 'voice_calling', name: 'Voice Calling' },
                        { key: 'chat_system', name: 'Chat System' },
                        { key: 'file_sharing', name: 'File Sharing' }
                    ]}
                />

                <FeatureCategory
                    title="Medical Features"
                    description="Configure medical record and health tracking features"
                    features={[
                        { key: 'medical_records', name: 'Medical Records' },
                        { key: 'prescription_management', name: 'Prescription Management' },
                        { key: 'lab_reports', name: 'Lab Reports' },
                        { key: 'medical_imaging', name: 'Medical Imaging' },
                        { key: 'health_tracking', name: 'Health Tracking' },
                        { key: 'medication_reminders', name: 'Medication Reminders' },
                        { key: 'vital_signs_tracking', name: 'Vital Signs Tracking' },
                        { key: 'medical_alerts', name: 'Medical Alerts' }
                    ]}
                />

                <FeatureCategory
                    title="Administrative Features"
                    description="Configure admin dashboard and management features"
                    features={[
                        { key: 'admin_dashboard', name: 'Admin Dashboard' },
                        { key: 'user_management', name: 'User Management' },
                        { key: 'doctor_management', name: 'Doctor Management' },
                        { key: 'appointment_management', name: 'Appointment Management' },
                        { key: 'payment_management', name: 'Payment Management' },
                        { key: 'report_generation', name: 'Report Generation' },
                        { key: 'analytics_dashboard', name: 'Analytics Dashboard' },
                        { key: 'system_settings', name: 'System Settings' }
                    ]}
                />

                <FeatureCategory
                    title="Security Features"
                    description="Configure security and access control features"
                    features={[
                        { key: 'two_factor_authentication', name: 'Two-Factor Authentication' },
                        { key: 'data_encryption', name: 'Data Encryption' },
                        { key: 'audit_logs', name: 'Audit Logs' },
                        { key: 'role_based_access', name: 'Role-Based Access' },
                        { key: 'session_management', name: 'Session Management' },
                        { key: 'password_policy', name: 'Password Policy' }
                    ]}
                />

                <FeatureCategory
                    title="Additional Features"
                    description="Configure additional platform features"
                    features={[
                        { key: 'multi_language_support', name: 'Multi-Language Support' },
                        { key: 'dark_mode', name: 'Dark Mode' },
                        { key: 'mobile_app', name: 'Mobile App' },
                        { key: 'offline_mode', name: 'Offline Mode' },
                        { key: 'api_integration', name: 'API Integration' },
                        { key: 'third_party_integrations', name: 'Third-Party Integrations' },
                        { key: 'backup_restore', name: 'Backup & Restore' },
                        { key: 'system_monitoring', name: 'System Monitoring' }
                    ]}
                />
            </div>
        </div>
    );
};

export default Features;
